/** 前端通用工具函数 */

export const MEETING_TYPE_LABEL: Record<string, string> = {
  DAILY: "日会",
  WEEKLY: "周会",
  MONTHLY: "月会",
};

export const ROLE_LABEL: Record<string, string> = {
  ADMIN: "管理员",
  DEPT_LEAD: "部门负责人",
  MEMBER: "成员",
};

const AVATAR_COLORS = ["#0082ef", "#7c3aed", "#0d9463", "#ea580c", "#db2777", "#0891b2", "#4f46e5"];

/** 与后端 avatarColor 一致：由姓名确定性取色 */
export function avatarColor(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** 今天 → 「今天 HH:mm」；本周 → 「M月D日 HH:mm」；更早 → 「YYYY年M月D日」 */
export function formatArchiveTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const hhmm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `今天 ${hhmm}`;

  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  if (d > weekAgo) return `${d.getMonth() + 1}月${d.getDate()}日 ${hhmm}`;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/** 取时刻 HH:mm */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 今天创建的归档标 NEW（按业务日期，而非入库时间） */
export function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** 今日业务日期键 YYYY-MM-DD（与后端口径一致） */
export function todayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * 大模型输出按白名单放行（仅 b/i/strong/em），其余标签剥离，
 * 既保留小结的强调排版，又避免注入风险。
 */
export function sanitizeRichText(html: string): string {
  const allowed = ["b", "i", "strong", "em"];
  return html.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (match, tag: string) => {
    return allowed.includes(tag.toLowerCase()) ? match : "";
  });
}

/** 当前 ISO 周键（与后端口径一致），如 2026-W42 */
export function isoWeekKey(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** 本周/上周/本月的周期参数（供跟进报告切换） */
export function periodOptions(): { label: string; value: string }[] {
  const now = new Date();
  const lastWeek = new Date(now);
  lastWeek.setDate(lastWeek.getDate() - 7);
  return [
    { label: `本周（${isoWeekKey(now)}）`, value: isoWeekKey(now) },
    { label: `上周（${isoWeekKey(lastWeek)}）`, value: isoWeekKey(lastWeek) },
    { label: "本月", value: `${now.getFullYear()}-${pad(now.getMonth() + 1)}` },
  ];
}
